import { useCallback, useMemo } from "react"
import { useQuizStore } from "@/stores/quiz-store"
import { trackEvent } from "@/features/analytics"
import { resolveStepDescriptor } from "@/features/quiz"
import type { QuizConfig, QuizStep } from "../types"

export function useQuiz(config: QuizConfig) {
  const { currentStepIndex, answers, setStepIndex, setAnswer, complete } = useQuizStore()

  const visibleSteps = useMemo(() => {
    return config.steps.filter((step) => {
      if (!step.showIf) return true
      return step.showIf(answers)
    })
  }, [config.steps, answers])

  const currentStep: QuizStep | undefined = visibleSteps[currentStepIndex]
  const totalSteps = visibleSteps.length

  // Progress counter reflects only "question" steps — cover, interstitials,
  // processing and email opt out via `countsTowardProgress: false` so the
  // header matches the design's `n/total`.
  const countedSteps = useMemo(
    () => visibleSteps.filter((s) => s.countsTowardProgress !== false),
    [visibleSteps],
  )
  const countedTotal = countedSteps.length
  const currentCounts = currentStep ? currentStep.countsTowardProgress !== false : false
  const countedCurrent = currentStep
    ? countedSteps.findIndex((s) => s.id === currentStep.id) + 1
    : 0
  const progress = totalSteps > 0 ? (currentStepIndex + 1) / totalSteps : 0
  const isFirst = currentStepIndex === 0
  const isLast = currentStepIndex === totalSteps - 1

  const currentValue = currentStep ? answers[currentStep.id] : undefined

  const canProceed = useMemo(() => {
    if (!currentStep) return false
    const { validate } = resolveStepDescriptor(config, currentStep)
    return validate ? validate(currentValue, currentStep) : true
  }, [config, currentStep, currentValue])

  const goNext = useCallback(() => {
    if (!currentStep) return false

    // Named funnel event (data-driven). Read answers FRESH from the store: for an
    // auto-advancing choice this runs from a delayed handler whose closure predates
    // the just-made selection, so `gender`/`age` params would otherwise be stale.
    if (currentStep.event) {
      trackEvent(currentStep.event, {
        metadata: currentStep.eventParams?.(useQuizStore.getState().answers),
      })
    }

    if (currentStep.nextStepId) {
      const nextId =
        typeof currentStep.nextStepId === "function"
          ? currentStep.nextStepId(answers)
          : currentStep.nextStepId

      if (nextId === null) {
        complete()
        return true
      }

      const idx = visibleSteps.findIndex((s) => s.id === nextId)
      if (idx >= 0) {
        setStepIndex(idx)
        return false
      }
    }

    if (isLast) {
      complete()
      return true
    }

    setStepIndex(currentStepIndex + 1)
    return false
  }, [
    currentStep,
    currentStepIndex,
    isLast,
    answers,
    visibleSteps,
    setStepIndex,
    complete,
  ])

  const goBack = useCallback(() => {
    // Skip back over "analysis"/processing screens — they auto-advance forward
    // on completion, so landing on one would bounce the user straight back.
    let idx = currentStepIndex - 1
    while (idx > 0 && visibleSteps[idx]?.type === "analysis") {
      idx -= 1
    }
    if (idx >= 0) {
      setStepIndex(idx)
    }
  }, [currentStepIndex, visibleSteps, setStepIndex])

  const setCurrentAnswer = useCallback(
    (value: string | string[]) => {
      if (currentStep) {
        setAnswer(currentStep.id, value)
      }
    },
    [currentStep, setAnswer],
  )

  return {
    currentStep,
    currentStepIndex,
    currentValue,
    totalSteps,
    countedCurrent,
    countedTotal,
    currentCounts,
    progress,
    isFirst,
    isLast,
    canProceed,
    goNext,
    goBack,
    setCurrentAnswer,
  }
}
